import React, {useMemo, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import fileDownload from 'js-file-download';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  CourseAssignmentSimpleResponse,
  CourseAssignmentStateEnum,
  GetCourseAssignmentListRequestV2} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {useExportCourseAssignmentsMutation} from 'src/redux/queries/courseAssignment';
import {ViewActionIcon} from 'src/design/molecules/ViewActionIcon';
import {ExportButton} from 'src/design/molecules/ExportButton';
import {Pill} from 'src/design/atoms/Pill';
import {
  CourseAssignmentEnrollmentPill,
  CourseAssignmentStatePill,
} from 'src/shared/constants/PillConstants';
import {Spinner} from 'src/design/atoms/Spinner';
import {
  CourseAssignmentEnrollmentDataOptions,
  CourseAssignmentStateDataOptions,
} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {useGetCourseAssignmentListV2Query} from 'src/redux/queries/courseAssignment';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface UserCourseAssignmentsTableProps {
  apiParams?: Partial<GetCourseAssignmentListRequestV2>;
  titleText?: string;
  enableExport?: boolean;
  enableRefresh?: boolean;
}

const UserCourseAssignmentsTable: React.FC<UserCourseAssignmentsTableProps> = ({
  apiParams,
  enableExport = false,
  enableRefresh = false,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [exportCourseAssignments] = useExportCourseAssignmentsMutation();

  const columns = useMemo<MRT_ColumnDef<CourseAssignmentSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableColumnFilter: false,
        enableSorting: false,
        enableColumnActions: false,
        size: 100,
        enableResizing: false,
        enableClickToCopy: false,
        Cell: ({row}) => (
          <Group gap="xs">
            <ViewActionIcon
              onClick={() =>
                navigate(
                  PathUtility.TrainingCourseAssignmentView(
                      String(row.original.courseDefinition?.id),
                      String(row.original.id),
                      'content'
                    )
                )
              }
              tooltipText="View Assignment"
              size="sm"
            />
          </Group>
        ),
      },
      {
        accessorKey: 'courseDefinition.name',
        header: 'Course Name',
        enableResizing: false,
      },
      {
        accessorKey: 'state',
        header: 'Training Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill
            {...CourseAssignmentStatePill(
              cell.getValue<CourseAssignmentStateEnum>()
            )}
          />
        ),
      },
      {
        accessorKey: 'active',
        header: 'Enrollment',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentEnrollmentDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...CourseAssignmentEnrollmentPill(cell.getValue<boolean>())} />
        ),
      },
      {
        id: 'assignedAt',
        accessorFn: row => (row.assignedAt ? new Date(row.assignedAt) : null),
        header: 'Assigned Date',
        filterVariant: 'date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        id: 'completedAt',
        accessorFn: row => (row.completedAt ? new Date(row.completedAt) : null),
        header: 'Complete Date',
        filterVariant: 'date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        id: 'expectedExpirationAt',
        accessorFn: row =>
          row.expectedExpirationAt ? new Date(row.expectedExpirationAt) : null,
        header: 'Renew Date',
        filterVariant: 'date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        id: 'dueAt',
        accessorFn: row => (row.dueAt ? new Date(row.dueAt) : null),
        header: 'Due Date',
        filterVariant: 'date',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | undefined | null>();
          if (value && new Date(value) < new Date()) {
            return <Box c="red">{value?.toString().toShortDate()}</Box>;
          } else {
            return <>{value && value?.toString().toShortDate()}</>;
          }
        },
      },
    ],
    [navigate]
  );

  const tableState = useTableState({
    identifier: 'user-course-assignments',
    columns: columns,
    defaultSorting: [{id: 'dueAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const [isExportLoading, setIsExportLoading] = useState(false);

  const apiRequest = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
    ...apiParams,
  };
  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseAssignmentListV2Query(apiRequest);
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowSelection: false,
      enableRowActions: false,
      columns: columns,
      data: rows,
      renderTopToolbarCustomActions: () => (
        <Group justify="space-between" gap={3}>
          {enableExport && (
            <PermissionGuard
              module={ModuleEnum.Training}
              operation={PermissionOperationEnum.Export}>
              <ExportButton
                onClick={handleExportAssignments}
                loading={isExportLoading}
                disabled={isExportLoading}
              />
            </PermissionGuard>
          )}
        </Group>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: enableRefresh ? refetch : undefined,
  });

  const handleExportAssignments = async () => {
    try {
      setIsExportLoading(true);

      const response = await exportCourseAssignments(apiRequest).unwrap();

      fileDownload(
        response!.data,
        `user_training_export_${new Date().toString().toShortDate()}.csv`
      );
      setAlert(`Successfully exported file.`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsExportLoading(false);
  };

  return !isLoading ? <>{table}</> : <Spinner loadingText="Loading...." />;
};

export default UserCourseAssignmentsTable;
